import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  X,
} from "lucide-react";
import CustomCheckbox from "@/shared/components/ui/Checkbox/CustomCheckbox";
import CustomModal from "@/shared/components/ui/Modal/CustomModal";
import { PrimaryButton } from "@/shared/components/ui/Button/PrimaryButton";
import {
  getMatchScoreReviewMeta,
  MATCH_SCORE_REVIEW_THRESHOLD,
} from "@/features/contacts/utils/matchScoreReview";
import { renderMatchScoreReasoning } from "@/shared/utils/contactConfig";

const contactId = (contact) =>
  String(contact?.id ?? contact?.contact_id ?? contact?.contactId ?? "");

const MatchRow = ({ contact, selected, onToggle }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const score = Number(contact.matchScore);
  const fitMeta = getMatchScoreReviewMeta(score);
  const isStrongMatch = fitMeta?.key === "strong";
  const isMediumMatch = fitMeta?.key === "medium";
  const fitLabel = fitMeta?.label ?? "Unscored";
  const fitTone = isStrongMatch
    ? "bg-success-50 text-success-500"
    : isMediumMatch
      ? "bg-warning-50 text-warning-500"
      : "bg-error-50 text-error-500";
  const name = contact.name || contact.full_name || "Unnamed lead";
  const emailBlocked = contact.emailVerificationAllowed === false;

  return (
    <details
      className={`rounded-xl border transition-colors ${
        selected
          ? "border-secondary/50 bg-secondary/5"
          : "border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900"
      }`}
      open={isExpanded}
      onToggle={(event) => setIsExpanded(event.currentTarget.open)}
    >
      <summary className="flex cursor-pointer list-none items-center gap-3 px-3 py-3 [&::-webkit-details-marker]:hidden">
        <div
          className="pt-1"
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <CustomCheckbox
            checked={selected}
            onChange={(event) => {
              event.stopPropagation();
              onToggle();
            }}
          />
        </div>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {name}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${fitTone}`}
            >
              {fitLabel}
            </span>
            {emailBlocked && (
              <span className="rounded-full bg-error-50 px-2 py-0.5 text-[0.65rem] font-semibold text-error-500">
                Email blocked
              </span>
            )}
          </span>
        </span>
        <span
          className={`shrink-0 rounded-lg px-2.5 py-1 text-sm font-bold tabular-nums ${fitTone}`}
        >
          {score}%
        </span>
        <ChevronDown
          size={18}
          aria-hidden="true"
          className={`shrink-0 text-gray-400 transition-transform duration-200 ${
            isExpanded ? "rotate-180" : ""
          }`}
        />
      </summary>
      <div className="border-t border-gray-200 px-12 pb-3 pt-2 text-xs dark:border-gray-800">
        <p className="text-gray-500 dark:text-gray-400">
          {[contact.company, contact.email].filter(Boolean).join(" · ") || "Lead details unavailable"}
        </p>
        <p className="mt-1 text-gray-400 dark:text-gray-500">
          {[contact.position, contact.industry, contact.currentlyEnrolledCampaignName]
            .filter(Boolean)
            .join(" · ") || "No additional lead information available"}
        </p>
        {contact.emailVerificationStatus && (
          <p className={`mt-2 ${emailBlocked ? "text-error-500" : "text-success-500"}`}>
            Email: {contact.emailVerificationStatus}
            {contact.emailVerificationReason ? ` — ${contact.emailVerificationReason}` : ""}
          </p>
        )}
        {contact.matchScoreReason && (
          <div className="mt-2.5 border-l-[3px] border-secondary pl-3 text-xs break-words">
            {renderMatchScoreReasoning(
              contact.matchScoreReason,
              `review-${contactId(contact) || "lead"}`,
            )}
          </div>
        )}
      </div>
    </details>
  );
};

const MatchSection = ({ title, count, icon, children, emptyText, action }) => (
  <section className="flex min-h-[260px] min-w-0 flex-1 flex-col gap-3 rounded-2xl border border-gray-200 bg-gray-50/40 p-3 dark:border-gray-800 dark:bg-gray-900/50">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
        {action}
        {icon}
        <span>{title}</span>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
          {count}
        </span>
      </div>
    </div>
    <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
      {count > 0 ? children : <p className="py-4 text-xs text-gray-500">{emptyText}</p>}
    </div>
  </section>
);

const MatchScoreReviewModal = ({
  isOpen,
  contacts = [],
  campaign,
  onClose,
  onBack,
  onConfirm,
  isEnrolling = false,
  backButtonLabel = "Back",
}) => {
  const [selectedIds, setSelectedIds] = useState(new Set());

  const strongMatches = useMemo(
    () => contacts.filter(
      (contact) => getMatchScoreReviewMeta(contact.matchScore)?.key === "strong",
    ),
    [contacts],
  );
  const mediumMatches = useMemo(
    () => contacts.filter(
      (contact) =>
        getMatchScoreReviewMeta(contact.matchScore)?.key === "medium",
    ),
    [contacts],
  );
  const noMatches = useMemo(
    () => contacts.filter(
      (contact) => getMatchScoreReviewMeta(contact.matchScore)?.key === "no_match",
    ),
    [contacts],
  );
  const failedMatches = useMemo(
    () => contacts.filter((contact) => contact.scoreError),
    [contacts],
  );
  const emailBlockedMatches = useMemo(
    () => contacts.filter((contact) => contact.emailVerificationAllowed === false),
    [contacts],
  );
  const selectableContacts = useMemo(
    () => [...strongMatches, ...mediumMatches, ...noMatches].filter(
      (contact) => contact.emailVerificationAllowed !== false,
    ),
    [strongMatches, mediumMatches, noMatches],
  );
  const selectedContacts = useMemo(
    () => selectableContacts.filter((contact) => selectedIds.has(contactId(contact))),
    [selectableContacts, selectedIds],
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedIds(
        new Set(
          strongMatches
            .filter((contact) => contact.emailVerificationAllowed !== false)
            .map(contactId),
        ),
      );
    }
  }, [isOpen, strongMatches]);

  const toggleContact = (contact) => {
    const id = contactId(contact);
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllMatches = (matches) => {
    setSelectedIds((current) => {
      const eligibleMatches = matches.filter(
        (contact) => contact.emailVerificationAllowed !== false,
      );
      const allSelected = eligibleMatches.every((contact) =>
        current.has(contactId(contact)),
      );
      const next = new Set(current);

      eligibleMatches.forEach((contact) => {
        const id = contactId(contact);
        if (allSelected) next.delete(id);
        else next.add(id);
      });

      return next;
    });
  };

  const allMatchesSelected = (matches) =>
    matches.some((contact) => contact.emailVerificationAllowed !== false) &&
    matches
      .filter((contact) => contact.emailVerificationAllowed !== false)
      .every((contact) => selectedIds.has(contactId(contact)));
  const someMatchesSelected = (matches) =>
    matches.some(
      (contact) =>
        contact.emailVerificationAllowed !== false &&
        selectedIds.has(contactId(contact)),
    ) &&
    !allMatchesSelected(matches);

  const handleConfirm = () => {
    if (selectedContacts.length === 0 || failedMatches.length > 0 || isEnrolling) return;
    onConfirm(selectedContacts);
  };

  return (
    <CustomModal
      isOpen={isOpen}
      width="960px"
      className="p-6 max-h-[calc(100dvh-60px)]"
    >
      <div className="flex min-h-0 w-full flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onBack}
                disabled={isEnrolling}
                className="rounded-full p-1 text-gray-500 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label={backButtonLabel}
                title={backButtonLabel}
              >
                <ArrowLeft size={18} />
              </button>
              <p className="text-xs font-semibold uppercase tracking-wide text-secondary">
                Match score review
              </p>
            </div>
            <h2 className="mt-1 text-xl font-bold text-gray-900 dark:text-gray-100">
              Review leads for {campaign?.name || "this campaign"}
            </h2>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Strong matches are selected by default. Medium and no matches stay visible so you can decide.
              {campaign?.name ? ` Scores are for ${campaign.name}; the Contacts table shows each lead's best score across campaigns.` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isEnrolling}
            className="rounded-full p-1 text-gray-500 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Close match score review"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          <div className="rounded-xl border border-success-500/20 bg-success-50/60 px-3 py-2">
            <p className="text-xs text-success-500">Strong Matches</p>
            <p className="text-lg font-bold text-success-500">{strongMatches.length}</p>
          </div>
          <div className="rounded-xl border border-warning-500/20 bg-warning-50/60 px-3 py-2">
            <p className="text-xs text-warning-500">Medium Matches</p>
            <p className="text-lg font-bold text-warning-500">{mediumMatches.length}</p>
          </div>
          <div className="rounded-xl border border-error-500/20 bg-error-50/60 px-3 py-2">
            <p className="text-xs text-error-500">No Matches</p>
            <p className="text-lg font-bold text-error-500">{noMatches.length}</p>
          </div>
          <div className={`rounded-xl border px-3 py-2 ${emailBlockedMatches.length ? "border-error-500/20 bg-error-50" : "border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-800/50"}`}>
            <p className="text-xs text-gray-500">Email Domain Issues</p>
            <p className={`text-lg font-bold ${emailBlockedMatches.length ? "text-error-500" : "text-gray-900 dark:text-gray-100"}`}>
              {emailBlockedMatches.length}
            </p>
          </div>
        </div>

        {failedMatches.length > 0 && (
          <div className="rounded-xl border border-error-500/20 bg-error-50 px-3 py-2 text-xs text-error-500">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>
                {failedMatches.length} selected lead{failedMatches.length !== 1 ? "s" : ""} could not receive a valid score. Enrollment is blocked until every selected lead has a score.
              </span>
            </div>
            <div className="mt-2 space-y-1 pl-6">
              {failedMatches.map((contact) => (
                <p key={contactId(contact)}>
                  {contact.name || contact.full_name || "Unnamed lead"}: {contact.scoreError}
                </p>
              ))}
            </div>
          </div>
        )}

        {emailBlockedMatches.length > 0 && (
          <div className="rounded-xl border border-error-500/20 bg-error-50 px-3 py-2 text-xs text-error-500">
            <div className="flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>
                {emailBlockedMatches.length} lead{emailBlockedMatches.length !== 1 ? "s have" : " has"} an email that cannot be validated for sending and will not be enrolled.
              </span>
            </div>
            <div className="mt-2 space-y-1 pl-6">
              {emailBlockedMatches.map((contact) => (
                <p key={contactId(contact)}>
                  {contact.name || contact.full_name || "Unnamed lead"}: {contact.emailVerificationReason || contact.emailVerificationStatus}
                </p>
              ))}
            </div>
          </div>
        )}

        <div className="grid min-h-0 grid-cols-1 gap-4 overflow-y-auto lg:grid-cols-3">
          <MatchSection
            title="Strong Matches"
            count={strongMatches.filter((contact) => contact.emailVerificationAllowed !== false).length}
            icon={<CheckCircle2 size={16} className="text-success-500" />}
            emptyText={`No eligible leads scored above ${MATCH_SCORE_REVIEW_THRESHOLD - 1}%.`}
          >
            {strongMatches.filter((contact) => contact.emailVerificationAllowed !== false).map((contact) => (
              <MatchRow
                key={contactId(contact)}
                contact={contact}
                selected={selectedIds.has(contactId(contact))}
                onToggle={() => toggleContact(contact)}
              />
            ))}
          </MatchSection>
          <MatchSection
            title="Medium Matches"
            count={mediumMatches.filter((contact) => contact.emailVerificationAllowed !== false).length}
            icon={<AlertTriangle size={16} className="text-warning-500" />}
            emptyText="No eligible medium-scored leads."
            action={
              mediumMatches.some((contact) => contact.emailVerificationAllowed !== false) ? (
                <div
                  className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300"
                  title="Select all medium matches"
                >
                  <CustomCheckbox
                    checked={allMatchesSelected(mediumMatches)}
                    indeterminate={someMatchesSelected(mediumMatches)}
                    onChange={() => selectAllMatches(mediumMatches)}
                    disabled={isEnrolling}
                  />
                  <span>Select all</span>
                </div>
              ) : null
            }
          >
            {mediumMatches.filter((contact) => contact.emailVerificationAllowed !== false).map((contact) => (
              <MatchRow
                key={contactId(contact)}
                contact={contact}
                selected={selectedIds.has(contactId(contact))}
                onToggle={() => toggleContact(contact)}
              />
            ))}
          </MatchSection>
          <MatchSection
            title="No Matches"
            count={noMatches.filter((contact) => contact.emailVerificationAllowed !== false).length}
            icon={<AlertTriangle size={16} className="text-error-500" />}
            emptyText="No eligible no-match leads."
            action={
              noMatches.some((contact) => contact.emailVerificationAllowed !== false) ? (
                <div
                  className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300"
                  title="Select all no matches"
                >
                  <CustomCheckbox
                    checked={allMatchesSelected(noMatches)}
                    indeterminate={someMatchesSelected(noMatches)}
                    onChange={() => selectAllMatches(noMatches)}
                    disabled={isEnrolling}
                  />
                  <span>Select all</span>
                </div>
              ) : null
            }
          >
            {noMatches.filter((contact) => contact.emailVerificationAllowed !== false).map((contact) => (
              <MatchRow
                key={contactId(contact)}
                contact={contact}
                selected={selectedIds.has(contactId(contact))}
                onToggle={() => toggleContact(contact)}
              />
            ))}
          </MatchSection>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
          <PrimaryButton
            onClick={handleConfirm}
            disabled={
              isEnrolling || selectedContacts.length === 0 || failedMatches.length > 0
            }
            isLoading={isEnrolling}
            startContent={false}
          >
            {isEnrolling
              ? "Adding..."
              : `Add ${selectedContacts.length} Lead${selectedContacts.length !== 1 ? "s" : ""} to Campaign`}
          </PrimaryButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default MatchScoreReviewModal;
